import * as Sentry from '@sentry/react';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';

interface LocalStorageObject {
  /**
   * Show the banner.
   */
  banner: string;
  /**
   * The organization ID the user is authenticated to.
   */
  organization_id: string;
  /**
   * The user ID of the user that is signed in.
   */
  user_id: string;
  /**
   * The name of the organization the user is authenticated for.
   */
  organization_name: string;
  /**
   * The email of the user that is signed in.
   */
  user_email: string;
  /**
   * The default portfolio ID for the organization the user is authenticated for.
   */
  default_portfolio_id: string;
  /**
   * The default retailer for the organization the user is authenticated for.
   */
  default_retailer: string;
}

/**
 * Sets the local storage object.
 * @param object - The object to set in local storage.
 */
function setLocalStorage(object: Partial<LocalStorageObject>) {
  for (const [key, value] of Object.entries(object)) {
    try {
      if (!value) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Authentication,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].localStorage,
        },
        extra: { key, operation: !value ? 'removeItem' : 'setItem' },
      });
    }
  }
}

/**
 * Removes the local storage object.
 * @param object - The object to remove from local storage.
 */
function removeLocalStorage(keys: (keyof LocalStorageObject)[]) {
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Authentication,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].localStorage,
        },
        extra: { key, operation: 'removeItem' },
      });
    }
  }
}

/**
 * Gets the local storage object.
 * @param keys - The keys to get from local storage.
 * @returns The value of the keys in local storage.
 */
function getLocalStorage(keys: (keyof LocalStorageObject)[]) {
  return keys.reduce(
    (acc, key) => {
      try {
        const value = localStorage.getItem(key);

        if (!value) {
          return acc;
        }

        acc[key] = value;

        return acc;
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Authentication,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].localStorage,
          },
          extra: { key, operation: 'getItem' },
        });
        return acc;
      }
    },
    {} as Partial<LocalStorageObject>,
  );
}

export { getLocalStorage, removeLocalStorage, setLocalStorage };
